/**
 * „Geplante Mails": Planung, Feedback-Links, Kommentare, Änderungsvorschläge
 * und Versionen (Migration 0046). Die Datenbank liefert fertige Zeilen
 * (mail_planung_liste, mail_link_uebersicht …); hier stehen nur Typen,
 * Abrufe und Helfer für Wochen, Daten und Beschriftungen.
 *
 * Zwei Zugänge:
 * - angemeldet (Team, Geschäftsführung, Studioleitung): mail_planung_* /
 *   mail_feedback_* — Rechte prüft die Datenbank;
 * - Feedback-Link: mail_link_* mit dem Token der Ansprechperson — es gehört
 *   in keine Logs. Ansehen, Kommentieren und Ändern gehen nur, wenn man mit
 *   dem Google-Konto der Adresse der Person (@randori-pro.de) angemeldet ist
 *   (mail_link_anmeldung, Feld „anmeldung“ in den Antworten, Migration 0051).
 *   Admins lesen jeden Link mit (Migration 0052, anmeldung.status
 *   'admin_ansicht'): nur ansehen, schreiben geht weiter nur als die Person.
 */
import { supabase, invokeFunction, edgeFunctions, type CampaignStatus, type ReviewState, type BrevoDraftResult } from './supabase';
import type { Aenderung, BlockSchluessel } from './zusammenarbeit';

// ── Typen (Formen wie in Migration 0046, Feldnamen exakt) ───────────────────

export type PlanungStand = 'in_arbeit' | 'feedback' | 'einarbeiten' | 'fertig';
export type VersandArt = 'versendet' | 'eingeplant' | 'angehalten' | 'geplant' | 'wunsch' | 'aus_name';
export type EventQuelle = 'planung' | 'event_manager' | 'assistent' | 'name';
export type AnfrageAntwort = 'offen' | 'kommentiert' | 'passt';
export type FeedbackArt = 'kommentar' | 'vorschlag' | 'passt';
export type AutorArt = 'extern' | 'team' | 'intern';
export type FeedbackErgebnis = 'erledigt' | 'uebernommen' | 'abgelehnt';
export type PersonSicht = 'zugeordnet' | 'sportschulen' | 'alle';
export type VersionQuelle = 'ausgangsstand' | 'angelegt' | 'bearbeitet' | 'vorschlag' | 'wiederhergestellt' | 'system';

export interface PlanungsAnfrage {
  person_id: string;
  name: string;
  antwort: AnfrageAntwort;
  gesehen_am: string | null;
  angefragt_am: string;
  // nur für das Team (mail_planung_mail)
  token?: string;
  email?: string | null;
  rolle_text?: string | null;
  nachricht?: string | null;
  eingeladen_am?: string | null;
  sicht?: PersonSicht;
  /** nur Team: Link noch gültig? (false = gesperrt, nicht mehr teilen) */
  aktiv?: boolean;
  angefragt_von_name?: string | null;
}

export interface Empfaengerkreis {
  gruppen: { name: string; anzahl: number }[];
  summe: number;
  /** Mehrere Zielgruppen: Summe ist die Obergrenze (Brevo schickt jedem Postfach nur einmal). */
  mehrere: boolean;
  manuell: number;
  /** Interne Kopie (Namen der Verteiler) */
  intern: string[];
}

export interface Versandstatistik {
  gesendet: number; zugestellt: number; geoeffnet: number; geklickt: number;
  abgemeldet: number; bounces: number; stand: string;
}

export interface PlanungsMail {
  id: string;
  name: string;
  betreff: string;
  vorschau_text: string | null;
  status: CampaignStatus;
  review_state: ReviewState | null;
  requires_approval: boolean;
  aus_assistent: boolean;
  design_mode: 'html' | 'template' | null;
  brevo_id: number | null;
  brevo_status: string | null;
  brevo_status_at: string | null;
  brevo_synced_at: string | null;
  inhalt_geaendert_am: string | null;
  brevo_veraltet: boolean;
  /**
   * brevo_am (Migration 0051): nur bei „angehalten“, wenn unter „Termine &
   * Stand“ ein anderer Termin eingetragen ist — dann steht in `am` der
   * eingetragene und hier der alte Termin aus Brevo.
   */
  versand: { am: string | null; art: VersandArt | null; brevo_am?: string | null };
  event: { titel: string | null; beginn: string | null; ende: string | null; quelle: EventQuelle | null };
  sportschulen: string[];
  uebergreifend: boolean;
  empfaenger: Empfaengerkreis;
  statistik: Versandstatistik | null;
  stand: PlanungStand;
  feedback_bis: string | null;
  notiz?: string | null;
  ausgeblendet: boolean;
  zaehler: { kommentare: number; offen: number; vorschlaege_offen: number; ungelesen: number; passt: number };
  anfragen: PlanungsAnfrage[];
  letzte_aktivitaet: string | null;
  erstellt_von?: string | null;
  meine_anfrage?: { antwort: AnfrageAntwort; gesehen_am: string | null; nachricht: string | null; angefragt_von_name: string | null } | null;
  /** nur Team (Migration 0047): eingetragene Planungswerte ohne Ableitung, null = keine Planungszeile */
  planung?: { event_titel: string | null; event_beginn: string | null; event_ende: string | null; versand_geplant: string | null } | null;
  /** Migration 0050: darf diese Person die Mail löschen? (wie brevo-draft „loeschen“: wer sie bearbeiten darf, nicht versendet) */
  darf_loeschen?: boolean;
}

/** Eine gelöschte Mail (mail_planung_geloescht, Migration 0050). */
export interface GeloeschteMail {
  id: string;
  name: string;
  betreff: string | null;
  geloescht_am: string;
  geloescht_von_name: string | null;
  /** Status vor dem Löschen — dahin führt Wiederherstellen zurück (eingeplant → Entwurf) */
  status_vorher: CampaignStatus | null;
  sportschulen: string[];
  /** Hatte eine Brevo-Kampagne — die ist gelöscht und muss nach dem Wiederherstellen neu angelegt werden. */
  war_in_brevo: boolean;
  darf_wiederherstellen: boolean;
}

/** Ergebnis von brevo-draft „loeschen“. */
export interface LoeschErgebnis {
  ok: boolean;
  result: 'geloescht';
  /** geloescht = Brevo-Kampagne mitgelöscht, keine = gab es nicht, schon_weg = war in Brevo schon gelöscht */
  brevo: 'geloescht' | 'keine' | 'schon_weg';
  schon_geloescht?: boolean;
}

/**
 * Zeichenstift-Markierung. x relativ zur Mitte der Mail; y ab der Oberkante
 * des Anker-Blocks (anker des Eintrags), ohne Anker ab dem oberen Rand.
 */
export interface Markierung {
  ansicht: 'computer' | 'handy';
  breite: number;
  hoehe: number;
  farbe: string;
  striche: [number, number][][];
}

export interface Anker extends BlockSchluessel { text: string }

export interface FeedbackEintrag {
  id: string;
  art: FeedbackArt;
  antwort_auf: string | null;
  text: string | null;
  zitat: string | null;
  anker: Anker | null;
  markierung: Markierung | null;
  aenderungen: Aenderung[] | null;
  betreff_vorschlag: string | null;
  betreff_ergebnis: 'uebernommen' | 'abgelehnt' | null;
  basis_md5: string | null;
  basis_version: number | null;
  autor_name: string;
  autor_art: AutorArt;
  ist_meins: boolean;
  ergebnis: FeedbackErgebnis | null;
  erledigt_am: string | null;
  erledigt_von_name: string | null;
  gelesen: boolean;
  created_at: string;
}

/** Eintrag im Reiter „Rückmeldungen" (über alle Mails). */
export interface FeedbackMitMail extends FeedbackEintrag {
  campaign_id: string;
  mail_name: string;
  mail_betreff: string;
}

export interface VersionKopf {
  nr: number;
  quelle: VersionQuelle;
  notiz: string | null;
  autor_name: string | null;
  created_at: string;
  updated_at: string;
  /** Prüfsumme aus HTML, Betreff und Vorschautext (mail_stand_md5, Migration 0047) */
  md5: string;
  betreff: string | null;
  vorschau_text?: string | null;
}

export interface VersionVoll {
  nr: number;
  betreff: string | null;
  vorschau_text: string | null;
  html: string | null;
  quelle: VersionQuelle;
  notiz: string | null;
  autor_name: string | null;
  created_at: string;
}

/** Warum eine Person die Mail über ihren Link sieht (mail_planung_mail, Migration 0052). */
export type SichtbarGrund = 'anfrage' | 'sportschule' | 'alle';

/**
 * Eine aktive Ansprechperson, die diese Mail über ihren persönlichen Link
 * sieht — angefragt, über ihre Sportschule oder mit „alle Mails“.
 * token, person_id und zuletzt_gesehen nur, wenn die angemeldete Person die
 * Ansprechpersonen lesen darf (Team) — sonst null.
 */
export interface SichtbarFuer {
  person_id: string | null;
  name: string;
  rolle_text: string | null;
  herkunft: PersonHerkunft;
  sicht: PersonSicht;
  grund: SichtbarGrund;
  token: string | null;
  zuletzt_gesehen: string | null;
  /** Sportschulen, über die die Person die Mail sieht (bei grund 'sportschule'; fehlt sie, gelten die der Mail) */
  sportschulen?: string[];
}

export interface PlanungsDetail extends PlanungsMail {
  html: string;
  /** Prüfsumme des angezeigten Stands (HTML, Betreff, Vorschautext) — Basis für „inzwischen geändert“ */
  md5: string;
  /** nur mail_planung_mail (Migration 0047): darf diese Person den Inhalt ändern (übernehmen, direkt bearbeiten, wiederherstellen)? */
  darf_inhalt_aendern?: boolean;
  version_nr: number;
  feedback: FeedbackEintrag[];
  versionen: VersionKopf[];
  /**
   * nur mail_planung_mail (Migration 0052): wer die Mail über den eigenen
   * Link sieht. Reihenfolge: Anfrage, Sportschule, alle; darin nach Name.
   * Fehlt bei älterem Stand.
   */
  sichtbar_fuer?: SichtbarFuer[];
}

export interface LinkPerson {
  id?: string;
  name: string;
  rolle_text: string | null;
  sicht: PersonSicht;
  sportschulen: string[];
}

/** Neuer Eintrag (Kommentar, „passt so", Vorschlag, Antwort). */
export interface FeedbackDaten {
  art: FeedbackArt;
  text?: string | null;
  zitat?: string | null;
  anker?: Anker | null;
  markierung?: Markierung | null;
  aenderungen?: Aenderung[] | null;
  betreff_vorschlag?: string | null;
  basis_md5?: string | null;
  basis_version?: number | null;
  antwort_auf?: string | null;
}

export interface Ansprechperson {
  id: string;
  name: string;
  email: string | null;
  rolle_text: string | null;
  sicht: PersonSicht;
  sportschulen: string[];
  token: string;
  aktiv: boolean;
  zuletzt_gesehen: string | null;
  created_at: string;
  updated_at: string;
  /**
   * Migration 0051: 'studioaccount' = vom stündlichen Abgleich aus dem
   * CRM-Studioaccount angelegt. Name, Adresse, Sicht und Sportschulen kommen
   * dann aus dem CRM (Schutzregel in der Datenbank) — das Team schaltet nur
   * ab/ein und beschriftet die Rolle.
   */
  herkunft: PersonHerkunft;
}

export type PersonHerkunft = 'manuell' | 'studioaccount';

export interface PersonVorschlag {
  name: string;
  email: string | null;
  rolle_text: string | null;
  sportschulen: string[];
}

export interface PlanungFelder {
  event_titel?: string | null;
  event_beginn?: string | null;
  event_ende?: string | null;
  versand_geplant?: string | null;
  stand?: PlanungStand;
  feedback_bis?: string | null;
  notiz?: string | null;
  ausgeblendet?: boolean;
}

// ── Beschriftungen ──────────────────────────────────────────────────────────

/**
 * Farben folgen derselben Regel wie „Nächster Schritt“: Orange = wir sind
 * dran, Blau = wir warten auf andere, Grün = abgestimmt.
 */
export const STAND_INFO: Record<PlanungStand, { label: string; badge: string; text: string }> = {
  in_arbeit:   { label: 'In Arbeit',              badge: 'badge-neutral', text: 'Die Mail wird noch vorbereitet.' },
  feedback:    { label: 'Wartet auf Feedback',    badge: 'badge-info',    text: 'Ansprechpersonen sind gefragt — die Rückmeldungen stehen noch aus.' },
  einarbeiten: { label: 'Feedback einarbeiten',   badge: 'badge-warning', text: 'Es gibt Rückmeldungen, die noch eingearbeitet werden.' },
  fertig:      { label: 'Abgestimmt',             badge: 'badge-success', text: 'Alle sind einverstanden — die Mail kann raus.' },
};
export const STAENDE: PlanungStand[] = ['in_arbeit', 'feedback', 'einarbeiten', 'fertig'];

export const VERSAND_INFO: Record<VersandArt, { label: string; badge: string }> = {
  versendet:  { label: 'Versendet',              badge: 'badge-versendet' },
  eingeplant: { label: 'In Brevo eingeplant',    badge: 'badge-geplant' },
  angehalten: { label: 'In Brevo angehalten',    badge: 'badge-warning' },
  geplant:    { label: 'Versand geplant',        badge: 'badge-info' },
  wunsch:     { label: 'Wunschtermin',           badge: 'badge-neutral' },
  aus_name:   { label: 'Versand laut Mailname',  badge: 'badge-neutral' },
};

/** Was das Kalenderblatt bedeutet — als Hinweis beim Zeigen (dieselben Sätze wie in der Legende). */
export const KACHEL_BEDEUTUNG: Record<VersandArt, string> = {
  eingeplant: 'In Brevo eingeplant — geht von selbst raus',
  geplant: 'Versandtag unter „Termine“ eingetragen — in Brevo noch nicht eingeplant',
  wunsch: 'Nur Wunschtermin aus dem Assistenten — noch nicht fest eingetragen',
  aus_name: 'Nur aus dem Mailnamen abgeleitet — noch nicht fest eingetragen',
  angehalten: 'In Brevo angehalten — geht erst raus, wenn sie dort fortgesetzt wird',
  versendet: 'Versendet',
};

/**
 * Liegt zu der Mail eine Rückmeldung vor, um die sich jemand kümmern muss?
 * `neu`: ungelesene Einträge (rot) · sonst offene Kommentare und Vorschläge
 * (orange). null = nichts zu tun. Nur für das Team — Ansprechpersonen sehen
 * keine Zähler des Teams.
 */
export function rueckmeldungHinweis(m: Pick<PlanungsMail, 'zaehler'>): { zahl: number; neu: boolean; text: string } | null {
  const { offen, ungelesen } = m.zaehler;
  if (!(offen > 0) && !(ungelesen > 0)) return null;
  const neu = ungelesen > 0;
  const zahl = neu ? ungelesen : offen;
  const text = neu
    ? `${zahl} ${zahl === 1 ? 'neue Rückmeldung' : 'neue Rückmeldungen'}`
    : `${zahl} ${zahl === 1 ? 'offene Rückmeldung' : 'offene Rückmeldungen'}`;
  return { zahl, neu, text };
}

/** Brevo-Status in Klartext (null = noch nie gelesen oder kein Brevo-Entwurf). */
export function brevoStatusText(status: string | null | undefined): string {
  switch (status) {
    case 'draft': return 'Entwurf in Brevo';
    case 'queued': return 'In Brevo eingeplant';
    case 'suspended': return 'In Brevo angehalten';
    case 'in_process': return 'Brevo verschickt gerade';
    case 'sent': return 'Von Brevo versendet';
    case 'archive': return 'In Brevo archiviert';
    case null: case undefined: case '': return 'Noch nicht in Brevo';
    default: return `Brevo: ${status}`;
  }
}

export const ANTWORT_INFO: Record<AnfrageAntwort, { label: string; badge: string }> = {
  offen:       { label: 'Noch offen',  badge: 'badge-neutral' },
  kommentiert: { label: 'Hat kommentiert', badge: 'badge-info' },
  passt:       { label: 'Passt so',    badge: 'badge-success' },
};

/** „1 von 2 Antworten offen" bzw. „2 von 2 haben geantwortet". */
export function antwortenText(anfragen: { antwort: AnfrageAntwort }[]): string {
  const n = anfragen.length;
  const offen = anfragen.filter((a) => a.antwort === 'offen').length;
  if (offen > 0) return `${offen} von ${n} ${n === 1 ? 'Antwort' : 'Antworten'} offen`;
  return `${n} von ${n} ${n === 1 ? 'hat' : 'haben'} geantwortet`;
}

export const SICHT_INFO: Record<PersonSicht, { label: string; text: string }> = {
  zugeordnet:   { label: 'Nur zugeordnete Mails', text: 'Sieht nur die Mails, zu denen Du sie um Feedback bittest.' },
  sportschulen: { label: 'Mails ihrer Sportschulen', text: 'Sieht alle Mails der gewählten Sportschulen, dazu die zugeordneten.' },
  alle:         { label: 'Alle geplanten Mails', text: 'Sieht alle geplanten und kürzlich versendeten Mails.' },
};

export const VERSION_QUELLE: Record<VersionQuelle, string> = {
  ausgangsstand: 'Ausgangsstand',
  angelegt: 'Angelegt',
  bearbeitet: 'Bearbeitet',
  vorschlag: 'Vorschlag übernommen',
  wiederhergestellt: 'Wiederhergestellt',
  system: 'Automatisch',
};

// ── Datum und Wochen ────────────────────────────────────────────────────────

const WOCHENTAG = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const MONAT = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
export const MONATSNAMEN = MONAT;
export const WOCHENTAGE_KURZ = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

/** ISO-Datum (YYYY-MM-DD) oder Zeitstempel → lokales Datum ohne Uhrzeit. */
export function alsTag(wert: string | Date | null | undefined): Date | null {
  if (!wert) return null;
  if (wert instanceof Date) return new Date(wert.getFullYear(), wert.getMonth(), wert.getDate());
  const nurDatum = /^(\d{4})-(\d{2})-(\d{2})$/.exec(wert);
  if (nurDatum) return new Date(Number(nurDatum[1]), Number(nurDatum[2]) - 1, Number(nurDatum[3]));
  const d = new Date(wert);
  return Number.isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Lokales Datum → YYYY-MM-DD. */
export function tagIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function plusTage(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

/** Montag der Woche. */
export function wochenStart(d: Date): Date {
  const tag = (d.getDay() + 6) % 7; // Mo = 0
  return plusTage(alsTag(d)!, -tag);
}

/** ISO-Kalenderwoche. */
export function kalenderwoche(d: Date): { jahr: number; kw: number } {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const wt = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - wt);
  const jahrStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  return { jahr: t.getUTCFullYear(), kw: Math.ceil(((t.getTime() - jahrStart.getTime()) / 86_400_000 + 1) / 7) };
}

/** „Do 01.10." */
export function datumKurz(wert: string | Date | null | undefined): string {
  const d = alsTag(wert);
  if (!d) return '—';
  return `${WOCHENTAG[d.getDay()]} ${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.`;
}

/** „Do 01.10., 09:00" — Uhrzeit nur bei Zeitstempeln. */
export function datumZeit(wert: string | null | undefined): string {
  if (!wert) return '—';
  if (/^\d{4}-\d{2}-\d{2}$/.test(wert)) return datumKurz(wert);
  const d = new Date(wert);
  if (Number.isNaN(d.getTime())) return '—';
  return `${datumKurz(d)}, ${d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`;
}

/** „19.–23.10.2026" bzw. „Sa 10.10.2026". */
export function zeitraumText(beginn: string | null, ende: string | null): string {
  const a = alsTag(beginn);
  if (!a) return '—';
  const b = alsTag(ende);
  const tt = (d: Date) => String(d.getDate()).padStart(2, '0');
  const mm = (d: Date) => String(d.getMonth() + 1).padStart(2, '0');
  if (!b || b.getTime() === a.getTime()) return `${WOCHENTAG[a.getDay()]} ${tt(a)}.${mm(a)}.${a.getFullYear()}`;
  if (a.getFullYear() !== b.getFullYear()) return `${tt(a)}.${mm(a)}.${a.getFullYear()}–${tt(b)}.${mm(b)}.${b.getFullYear()}`;
  if (a.getMonth() !== b.getMonth()) return `${tt(a)}.${mm(a)}.–${tt(b)}.${mm(b)}.${b.getFullYear()}`;
  return `${tt(a)}.–${tt(b)}.${mm(b)}.${b.getFullYear()}`;
}

/** „heute", „morgen", „in 3 Tagen", „gestern", „vor 5 Tagen". */
export function tageText(wert: string | Date | null | undefined, heute: Date = new Date()): string {
  const d = alsTag(wert);
  if (!d) return '';
  const tage = Math.round((d.getTime() - alsTag(heute)!.getTime()) / 86_400_000);
  if (tage === 0) return 'heute';
  if (tage === 1) return 'morgen';
  if (tage > 1) return `in ${tage} Tagen`;
  return tage === -1 ? 'gestern' : `vor ${-tage} Tagen`;
}

/** „vor 5 Minuten", „vor 3 Stunden", sonst Datum mit Uhrzeit. */
export function wannText(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  const sek = Math.round((Date.now() - d.getTime()) / 1000);
  if (sek < 60) return 'gerade eben';
  if (sek < 3600) return `vor ${Math.round(sek / 60)} Min.`;
  if (sek < 86_400 && alsTag(d)!.getTime() === alsTag(new Date())!.getTime()) return `heute, ${d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`;
  return datumZeit(iso);
}

export function monatTitel(d: Date): string {
  return `${MONAT[d.getMonth()]} ${d.getFullYear()}`;
}

export type WochenGruppe = {
  /** stabile Kennung: 'verstrichen' | 'ohne' | 'versendet' | 'KW-2026-41' */
  key: string;
  titel: string;
  unter?: string;
  mails: PlanungsMail[];
};

/** Wonach die Liste sortiert: Versandtag der Mail oder Beginn des Events. */
export type Zeitbezug = 'versand' | 'event';

/** Datum, nach dem eine Mail einsortiert wird: Versandtag oder Event-Beginn. */
export function sortierTag(m: PlanungsMail, nach: Zeitbezug = 'versand'): Date | null {
  return alsTag(nach === 'event' ? m.event.beginn : m.versand.am) ?? null;
}

/** Gleicher Tag: nach Uhrzeit des Versands bzw. Event-Beginn, dann nach Name. */
function nachZeitVon(nach: Zeitbezug) {
  return (a: PlanungsMail, b: PlanungsMail) =>
    (nach === 'event' ? (a.event.beginn ?? '').localeCompare(b.event.beginn ?? '') : 0)
    || (a.versand.am ?? '').localeCompare(b.versand.am ?? '')
    || a.name.localeCompare(b.name, 'de');
}

/**
 * Anstehende Mails nach Wochen: zuerst „Termin verstrichen" (nicht versendet,
 * Versandtag vorbei — z. B. in Brevo angehalten), dann diese Woche, nächste
 * Woche …, am Ende „Ohne Versandtermin". Versendete Mails stehen nicht drin.
 *
 * nach = 'event': dieselben Wochen, aber nach Beginn des Events. Ein Event,
 * das gerade läuft, steht in „Diese Woche"; ist es schon vorbei (die Mail
 * aber nicht versendet), unter „Event vorbei"; ohne Event-Datum am Ende
 * unter „Ohne Event-Termin".
 */
export function wochenGruppen(mails: PlanungsMail[], heute: Date = new Date(), nach: Zeitbezug = 'versand'): WochenGruppe[] {
  const h = alsTag(heute)!;
  const dieseWoche = wochenStart(h);
  const verstrichen: PlanungsMail[] = [];
  const ohne: PlanungsMail[] = [];
  const wochen = new Map<string, { start: Date; mails: PlanungsMail[] }>();
  const event = nach === 'event';
  for (const m of mails) {
    if (m.status === 'sent') continue;
    let t = sortierTag(m, nach);
    if (!t) { ohne.push(m); continue; }
    // Beim Event zählt das Ende: Ein laufendes Event gehört in diese Woche.
    const bis = event ? alsTag(m.event.ende) ?? t : t;
    if (bis < h) { verstrichen.push(m); continue; }
    if (t < h) t = h;
    const start = wochenStart(t);
    const key = tagIso(start);
    if (!wochen.has(key)) wochen.set(key, { start, mails: [] });
    wochen.get(key)!.mails.push(m);
  }
  const nachZeit = nachZeitVon(nach);
  const gruppen: WochenGruppe[] = [];
  if (verstrichen.length) {
    gruppen.push(event
      ? { key: 'verstrichen', titel: 'Event vorbei', unter: 'Das Event ist schon vorbei, die Mail aber nicht versendet', mails: verstrichen.sort(nachZeit) }
      : { key: 'verstrichen', titel: 'Termin verstrichen', unter: 'Nicht versendet, obwohl der Tag vorbei ist — z. B. in Brevo angehalten', mails: verstrichen.sort(nachZeit) });
  }
  for (const [key, w] of [...wochen.entries()].sort(([a], [b]) => a.localeCompare(b))) {
    const { kw } = kalenderwoche(w.start);
    const diff = Math.round((w.start.getTime() - dieseWoche.getTime()) / (7 * 86_400_000));
    const titel = diff === 0 ? 'Diese Woche' : diff === 1 ? 'Nächste Woche' : `In ${diff} Wochen`;
    const ende = plusTage(w.start, 6);
    gruppen.push({
      key: `KW-${key}`,
      titel,
      unter: `KW ${kw} · ${datumKurz(w.start)} – ${datumKurz(ende)}`,
      mails: w.mails.sort(nachZeit),
    });
  }
  if (ohne.length) {
    gruppen.push(event
      ? { key: 'ohne', titel: 'Ohne Event-Termin', unter: 'Kein Event-Datum eingetragen oder im Namen erkannt', mails: ohne.sort(nachZeitVon('versand')) }
      : { key: 'ohne', titel: 'Ohne Versandtermin', unter: 'Noch kein Versandtag geplant', mails: ohne.sort((a, b) => a.name.localeCompare(b.name, 'de')) });
  }
  return gruppen;
}

/**
 * Versendete Mails nach Wochen, neueste zuerst — nach Versandtag oder (nach =
 * 'event') nach Event-Beginn; versendete Mails ohne Event-Datum stehen dann
 * am Ende unter „Ohne Event-Termin".
 */
export function versendetGruppen(mails: PlanungsMail[], nach: Zeitbezug = 'versand'): WochenGruppe[] {
  const wochen = new Map<string, { start: Date; mails: PlanungsMail[] }>();
  const ohne: PlanungsMail[] = [];
  for (const m of mails) {
    if (m.status !== 'sent') continue;
    const t = nach === 'event' ? sortierTag(m, 'event') : alsTag(m.versand.am) ?? new Date(0);
    if (!t) { ohne.push(m); continue; }
    const start = wochenStart(t);
    const key = tagIso(start);
    if (!wochen.has(key)) wochen.set(key, { start, mails: [] });
    wochen.get(key)!.mails.push(m);
  }
  const neuesteZuerst = (a: PlanungsMail, b: PlanungsMail) => -nachZeitVon(nach)(a, b);
  const gruppen: WochenGruppe[] = [...wochen.entries()].sort(([a], [b]) => b.localeCompare(a)).map(([key, w]) => ({
    key: `V-${key}`,
    titel: `KW ${kalenderwoche(w.start).kw}`,
    unter: `${nach === 'event' ? 'Events ' : ''}${datumKurz(w.start)} – ${datumKurz(plusTage(w.start, 6))}`,
    mails: w.mails.sort(neuesteZuerst),
  }));
  if (ohne.length) {
    gruppen.push({ key: 'V-ohne', titel: 'Ohne Event-Termin', unter: 'Versendet, ohne Event-Datum', mails: ohne.sort(neuesteZuerst) });
  }
  return gruppen;
}

/** Liegt ein Tag im Event-Zeitraum der Mail? */
export function eventAmTag(m: PlanungsMail, tag: Date): boolean {
  const a = alsTag(m.event.beginn);
  if (!a) return false;
  const b = alsTag(m.event.ende) ?? a;
  return tag >= a && tag <= b;
}

/** Empfänger in einem Satz: „ca. 2.556 Postfächer" (ohne Listen). */
export function empfaengerText(e: Empfaengerkreis): string {
  const n = e.summe + e.manuell;
  if (n === 0) return 'Noch keine Empfänger';
  const zahl = n.toLocaleString('de-DE');
  return `${e.mehrere ? 'bis zu ' : ''}${zahl} ${n === 1 ? 'Postfach' : 'Postfächer'}`;
}

export function prozent(teil: number, ganz: number): string {
  if (!ganz) return '–';
  return `${Math.round((teil / ganz) * 100)} %`;
}

// ── Nächster Schritt ────────────────────────────────────────────────────────

/**
 * erledigt = versendet oder in Brevo eingeplant (geht von selbst raus) ·
 * versandbereit = abgestimmt, nichts offen, Empfänger da — fehlt nur Brevo ·
 * brevo = abgestimmt, aber Brevo hat noch einen älteren Stand ·
 * termin = Versandtag fehlt oder ist verstrichen · warten = auf Antworten oder
 * Freigabe · handeln = Rückmeldungen lesen/einarbeiten, Empfänger, Stand.
 */
export type SchrittArt = 'erledigt' | 'versandbereit' | 'warten' | 'handeln' | 'brevo' | 'termin';
/** erfolg = grün, warnung = orange (wir sind dran), info = blau (wir warten), neutral = grau */
export type SchrittTon = 'erfolg' | 'warnung' | 'info' | 'neutral';

export interface NaechsterSchritt {
  art: SchrittArt;
  /** Ein Satz, was als Nächstes passiert oder zu tun ist */
  text: string;
  /** Kleingedrucktes dahinter (Termin, Quelle, Einzelheiten) */
  zusatz?: string;
  ton: SchrittTon;
}

/** „Anna", „Anna und Ben", „Anna, Ben und Carla", „Anna, Ben, Carla und 2 weiteren" */
export function namenAufzaehlung(namen: string[], max = 3): string {
  const kurz = namen.map(anredeName).filter(Boolean);
  if (kurz.length === 0) return '';
  if (kurz.length > max) return `${kurz.slice(0, max).join(', ')} und ${kurz.length - max} weiteren`;
  if (kurz.length === 1) return kurz[0];
  return `${kurz.slice(0, -1).join(', ')} und ${kurz[kurz.length - 1]}`;
}

/** Woher der Versandtag kommt, als Nachsatz („laut Mailname"). */
function terminQuelle(art: VersandArt | null): string {
  switch (art) {
    case 'aus_name': return ' (nur laut Mailname)';
    case 'wunsch': return ' (Wunschtermin)';
    default: return '';
  }
}

/** Abgestimmt: Stand „Abgestimmt" oder alle Gefragten haben „Passt so" gesagt. */
export function istAbgestimmt(m: PlanungsMail): boolean {
  return m.stand === 'fertig' || (m.anfragen.length > 0 && m.anfragen.every((a) => a.antwort === 'passt'));
}

/**
 * Der eine nächste Schritt einer Mail — für die Zeile auf jeder Karte, die
 * Schnellfilter und die Kennzahl „versandbereit". Reihenfolge: Erledigtes,
 * neue Rückmeldungen, Eingeplantes, offene Punkte, fehlende Empfänger,
 * Freigabe, verstrichener Termin, versandbereit, Warten auf Antworten,
 * fehlender Termin, Stand setzen, noch niemand gefragt.
 */
export function naechsterSchritt(m: PlanungsMail, heute: Date = new Date()): NaechsterSchritt {
  const h = alsTag(heute)!;
  const z = m.zaehler;
  const am = m.versand.am;
  const tag = alsTag(am);
  const plural = (n: number, eins: string, viele: string) => `${n} ${n === 1 ? eins : viele}`;

  if (m.status === 'sent') {
    return { art: 'erledigt', ton: 'erfolg', text: am ? `Versendet am ${datumKurz(am)} — nichts mehr zu tun` : 'Versendet — nichts mehr zu tun' };
  }
  if (m.status === 'sending' && m.brevo_status === 'in_process') {
    return { art: 'erledigt', ton: 'erfolg', text: 'Brevo verschickt die Mail gerade' };
  }
  if (m.status === 'failed') {
    return { art: 'handeln', ton: 'warnung', text: 'Versand fehlgeschlagen — in Brevo prüfen und neu einplanen' };
  }
  if (m.status === 'archived') {
    return { art: 'erledigt', ton: 'neutral', text: 'Archiviert — wird nicht versendet' };
  }
  if (z.ungelesen > 0) {
    return {
      art: 'handeln', ton: 'warnung',
      text: `${plural(z.ungelesen, 'neue Rückmeldung', 'neue Rückmeldungen')} ansehen`,
      zusatz: z.offen > 0 ? `${plural(z.offen, 'Punkt', 'Punkte')} noch offen` : undefined,
    };
  }
  if (m.versand.art === 'eingeplant') {
    return { art: 'erledigt', ton: 'erfolg', text: am ? `In Brevo eingeplant — geht ${datumZeit(am)} von selbst raus` : 'In Brevo eingeplant — geht von selbst raus' };
  }
  if (z.offen > 0) {
    return {
      art: 'handeln', ton: 'warnung',
      text: `${plural(z.offen, 'offenen Punkt', 'offene Punkte')} einarbeiten`,
      zusatz: z.vorschlaege_offen > 0 ? `davon ${plural(z.vorschlaege_offen, 'Änderungsvorschlag', 'Änderungsvorschläge')}` : undefined,
    };
  }
  if (m.empfaenger.summe + m.empfaenger.manuell === 0) {
    return { art: 'handeln', ton: 'warnung', text: 'Empfänger fehlen — Zielgruppe in der Kampagne wählen' };
  }
  if (m.status === 'pending_approval') {
    return { art: 'warten', ton: 'info', text: 'Wartet auf die Freigabe der Redaktion', zusatz: 'unter „Freigaben“' };
  }
  if (tag && tag < h) {
    return {
      art: 'termin', ton: 'warnung',
      text: 'Versandtermin verstrichen — neuen Termin setzen oder in Brevo freigeben',
      zusatz: `war ${datumKurz(am)}${m.versand.art === 'angehalten' ? ', in Brevo angehalten' : terminQuelle(m.versand.art)}`,
    };
  }

  const offeneAnfragen = m.anfragen.filter((a) => a.antwort === 'offen');
  if (istAbgestimmt(m)) {
    if (!am) return { art: 'termin', ton: 'warnung', text: 'Abgestimmt — Versandtermin festlegen' };
    if (m.brevo_veraltet) {
      return { art: 'brevo', ton: 'warnung', text: 'Abgestimmt — neueste Änderungen erst nach Brevo übertragen', zusatz: 'danach in Brevo einplanen' };
    }
    const unsicher = m.versand.art === 'aus_name' || m.versand.art === 'wunsch';
    const termin = `Versand ${datumZeit(am)}${terminQuelle(m.versand.art)}${unsicher ? ' — unter „Termine“ festlegen' : ''}`;
    if (m.versand.art === 'angehalten' || m.brevo_status === 'suspended') {
      const brevoAm = m.versand.brevo_am ?? null;
      return {
        art: 'versandbereit', ton: 'erfolg',
        text: 'Bereit zum Versand — in Brevo fortsetzen (dort angehalten)',
        zusatz: brevoAm ? `in Brevo noch ${datumZeit(brevoAm)}, dort auf ${datumZeit(am)} ändern` : termin,
      };
    }
    if (m.brevo_id) return { art: 'versandbereit', ton: 'erfolg', text: 'Bereit zum Versand — Entwurf in Brevo einplanen', zusatz: termin };
    return { art: 'versandbereit', ton: 'erfolg', text: 'Bereit zum Versand — in Brevo anlegen und einplanen', zusatz: termin };
  }

  if (offeneAnfragen.length > 0) {
    const wer = namenAufzaehlung(offeneAnfragen.map((a) => a.name));
    const frist = alsTag(m.feedback_bis);
    if (frist && frist < h) {
      return { art: 'warten', ton: 'warnung', text: `Rückmeldung von ${wer} überfällig — nachfragen`, zusatz: `war bis ${datumKurz(frist)} erbeten` };
    }
    return { art: 'warten', ton: 'info', text: `Wartet auf Rückmeldung von ${wer}${frist ? ` (bis ${datumKurz(frist)})` : ''}` };
  }
  if (m.stand === 'feedback' && m.anfragen.length === 0) {
    return { art: 'handeln', ton: 'warnung', text: 'Steht auf „Wartet auf Feedback“, aber niemand ist gefragt — Feedback anfragen' };
  }
  if (!am) return { art: 'termin', ton: 'warnung', text: 'Versandtermin festlegen', zusatz: 'unter „Termine“' };
  if (m.anfragen.length > 0) {
    return { art: 'handeln', ton: 'warnung', text: 'Alle haben geantwortet — Rückmeldungen prüfen und als abgestimmt markieren' };
  }
  if (z.kommentare > 0 || z.passt > 0) {
    return { art: 'handeln', ton: 'warnung', text: 'Rückmeldungen sind erledigt — als abgestimmt markieren oder weitere Personen fragen' };
  }
  return { art: 'handeln', ton: 'neutral', text: 'Noch niemand gefragt — Feedback anfragen oder als abgestimmt markieren' };
}

/** Schnellfilter: offen = es fehlt noch etwas; bereit = versandbereit; erledigt = versendet oder eingeplant. */
export type SchrittFilter = 'offen' | 'bereit';
export function passtZuSchritt(s: NaechsterSchritt, filter: SchrittFilter): boolean {
  return filter === 'bereit' ? s.art === 'versandbereit' : s.art !== 'versandbereit' && s.art !== 'erledigt';
}

// ── Links ───────────────────────────────────────────────────────────────────

/** Persönlicher Link einer Ansprechperson (Überblick oder eine Mail). */
export function feedbackLink(token: string, mailId?: string | null): string {
  return `${window.location.origin}/feedback/${token}${mailId ? `/${mailId}` : ''}`;
}

export function whatsappLink(text: string): string {
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}

export function mailtoLink(email: string | null | undefined, betreff: string, text: string): string {
  return `mailto:${email ?? ''}?subject=${encodeURIComponent(betreff)}&body=${encodeURIComponent(text)}`;
}

/** Text in die Zwischenablage; ohne Clipboard-API (http, ältere Browser) über ein Textfeld. */
export async function inZwischenablage(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const feld = document.createElement('textarea');
    feld.value = text;
    feld.setAttribute('readonly', '');
    feld.style.position = 'fixed';
    feld.style.opacity = '0';
    document.body.appendChild(feld);
    feld.select();
    let ok = false;
    try { ok = document.execCommand('copy'); } catch { ok = false; }
    feld.remove();
    return ok;
  }
}

/**
 * Feedback-Links gibt es nur für Adressen dieser Domain (Migration 0051):
 * Ansehen und Kommentieren geht nur nach der Google-Anmeldung mit genau der
 * Adresse der Person. Die Datenbank lehnt andere Adressen ab.
 */
export const ANMELDE_DOMAIN = 'randori-pro.de';

/** Adresse @randori-pro.de (ohne Groß-/Kleinschreibung, Leerzeichen am Rand egal)? */
export function istRandoriAdresse(email: string | null | undefined): boolean {
  return (email ?? '').trim().toLowerCase().endsWith(`@${ANMELDE_DOMAIN}`);
}

/** Hinweis am Adressfeld einer Ansprechperson. */
export const ADRESS_HINWEIS =
  `Nur Adressen @${ANMELDE_DOMAIN}: Die Person meldet sich damit über Google an, um ihre Mails zu sehen und zu kommentieren.`;

/**
 * Name für die Anrede: der Vorname — Studioaccounts („Randori-Pro Tegel“)
 * heißen wie ihre Sportschule, da bleibt der ganze Name.
 */
export function anredeName(name: string): string {
  const n = name.trim();
  if (/^randori-pro\s/i.test(n)) return n;
  return n.split(/\s+/)[0] ?? '';
}

/**
 * Kurzer Begleittext zum Teilen eines Links (WhatsApp, Mail). Ansehen und
 * Kommentieren gehen nur mit Google-Anmeldung unter der hinterlegten Adresse.
 */
export function teilenText(name: string, betreff: string | null, link: string): string {
  const anrede = `Hallo ${anredeName(name)}, `;
  const anmelden = `Zum Ansehen und Kommentieren meldest Du Dich dort mit Deinem Google-Konto @${ANMELDE_DOMAIN} an – mit der Adresse, unter der wir Dich kennen.`;
  return betreff
    ? `${anrede}magst Du Dir die Mail „${betreff}" anschauen? Du kannst direkt kommentieren, Stellen markieren oder Formulierungen ändern: ${link} ${anmelden}`
    : `${anrede}hier sind die geplanten Mails zum Abstimmen. Du kannst direkt kommentieren, Stellen markieren oder Formulierungen ändern: ${link} ${anmelden}`;
}

/** Initialen und eine ruhige Farbe aus dem Namen (für Avatare). */
export function initialen(name: string): string {
  const teile = name.trim().split(/\s+/).filter(Boolean);
  if (teile.length === 0) return '?';
  if (teile.length === 1) return teile[0].slice(0, 2).toUpperCase();
  return (teile[0][0] + teile[teile.length - 1][0]).toUpperCase();
}

const AVATAR_FARBEN = ['#6D1723', '#B45309', '#15803D', '#1D4ED8', '#7C3AED', '#0F766E', '#BE185D', '#4D7C0F', '#9A3412', '#334155'];
export function avatarFarbe(name: string): string {
  let h = 0;
  for (const z of name) h = (h * 31 + z.charCodeAt(0)) >>> 0;
  return AVATAR_FARBEN[h % AVATAR_FARBEN.length];
}

// ── Abrufe: angemeldet ──────────────────────────────────────────────────────

function client() {
  if (!supabase) throw new Error('Supabase nicht konfiguriert — .env prüfen');
  return supabase;
}

/** Fehler einer Datenbankfunktion; code = SQLSTATE (40001 = Mail inzwischen geändert). */
export class DatenbankFehler extends Error {
  readonly code: string | null;
  constructor(message: string, code: string | null) {
    super(message);
    this.name = 'DatenbankFehler';
    this.code = code;
  }
}

/** Wurde die Mail inzwischen geändert (40001 aus mail_inhalt_aendern / mail_version_wiederherstellen)? */
export function istKonflikt(e: unknown): boolean {
  return typeof e === 'object' && e !== null && (e as { code?: unknown }).code === '40001';
}

async function rpc<T>(name: string, args?: Record<string, unknown>): Promise<T> {
  const { data, error } = await client().rpc(name, args ?? {});
  if (error) throw new DatenbankFehler(error.message, error.code || null);
  return data as T;
}

// ── Einträge absichern: alte oder kaputte Zeilen dürfen die Anzeige nicht sprengen ──

const istText = (x: unknown): x is string => typeof x === 'string';
const istZahl = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x);
const istTexte = (x: unknown): x is string[] => Array.isArray(x) && x.every(istText);
const istObjekt = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x);

function ankerPruefen(a: unknown): Anker | null {
  if (!istObjekt(a) || !istText(a.tag) || !a.tag || !Number.isInteger(a.nr) || (a.nr as number) < 0 || !istText(a.text)) return null;
  return { tag: a.tag, nr: a.nr as number, text: a.text };
}

function markierungPruefen(m: unknown): Markierung | null {
  if (!istObjekt(m) || (m.ansicht !== 'computer' && m.ansicht !== 'handy') || !Array.isArray(m.striche)) return null;
  const punktOk = (p: unknown) => Array.isArray(p) && p.length === 2 && istZahl(p[0]) && istZahl(p[1]);
  if (!m.striche.every((s) => Array.isArray(s) && s.every(punktOk))) return null;
  return {
    ansicht: m.ansicht,
    breite: istZahl(m.breite) ? m.breite : 0,
    hoehe: istZahl(m.hoehe) ? m.hoehe : 0,
    farbe: istText(m.farbe) ? m.farbe : '',
    striche: m.striche as [number, number][][],
  };
}

function aenderungPruefen(a: unknown): Aenderung | null {
  if (!istObjekt(a) || !istObjekt(a.block) || !istText(a.block.tag) || !Number.isInteger(a.block.nr) || (a.block.nr as number) < 0) return null;
  const nk = a.nachher_knoten ?? null;
  const nh = a.nachher_html ?? null;
  if (!istText(a.vorher_text) || !istText(a.nachher_text) || !istTexte(a.vorher_knoten) || !(nk === null || istTexte(nk)) || !(nh === null || istText(nh))) return null;
  const e = a.ergebnis;
  const en = a.entfernen;
  const entfernen = istObjekt(en) && istText(en.art) && istTexte(en.bilder) ? { art: en.art, bilder: en.bilder } : null;
  return {
    block: { tag: a.block.tag, nr: a.block.nr as number },
    vorher_text: a.vorher_text,
    nachher_text: a.nachher_text,
    vorher_knoten: a.vorher_knoten,
    nachher_knoten: nk,
    nachher_html: nh,
    ...(entfernen ? { entfernen } : {}),
    ...(e === 'offen' || e === 'uebernommen' || e === 'abgelehnt' ? { ergebnis: e } : {}),
  };
}

/**
 * Ein Eintrag, wie ihn die Anzeige sicher verarbeiten kann: ungültiger Anker
 * oder ungültige Zeichnung → null, ungültige Stellen eines Vorschlags fallen
 * weg (die übrigen behalten in `pos` ihre gespeicherte Stelle). Ein Vorschlag
 * ohne gültige Stelle bleibt mit aenderungen = [].
 */
export function eintragPruefen<T extends FeedbackEintrag>(f: T): T {
  let aenderungen: Aenderung[] | null = null;
  if (Array.isArray(f.aenderungen)) {
    const gut: Aenderung[] = [];
    f.aenderungen.forEach((roh, pos) => {
      const a = aenderungPruefen(roh);
      if (a) gut.push(gut.length === pos ? a : { ...a, pos });
    });
    aenderungen = gut;
  }
  if (f.art === 'vorschlag') aenderungen ??= [];
  return {
    ...f,
    text: istText(f.text) ? f.text : null,
    zitat: istText(f.zitat) ? f.zitat : null,
    anker: ankerPruefen(f.anker),
    markierung: markierungPruefen(f.markierung),
    aenderungen,
    betreff_vorschlag: istText(f.betreff_vorschlag) && f.betreff_vorschlag ? f.betreff_vorschlag : null,
    autor_name: istText(f.autor_name) && f.autor_name.trim() ? f.autor_name : 'Unbekannt',
  };
}

const TOKEN_FORM = /^[0-9a-f]{64}$/;
const GRUENDE: readonly SichtbarGrund[] = ['anfrage', 'sportschule', 'alle'];

/** Ein Eintrag aus sichtbar_fuer — kaputte fallen weg, ein Token nur in gültiger Form. */
function sichtbarPruefen(x: unknown): SichtbarFuer[] {
  if (!istObjekt(x) || !istText(x.name) || !x.name.trim()) return [];
  const grund = GRUENDE.find((g) => g === x.grund);
  if (!grund) return [];
  const sicht: PersonSicht = x.sicht === 'alle' || x.sicht === 'sportschulen' ? x.sicht : 'zugeordnet';
  return [{
    person_id: istText(x.person_id) && x.person_id ? x.person_id : null,
    name: x.name.trim(),
    rolle_text: istText(x.rolle_text) && x.rolle_text.trim() ? x.rolle_text : null,
    herkunft: x.herkunft === 'studioaccount' ? 'studioaccount' : 'manuell',
    sicht,
    grund,
    token: istText(x.token) && TOKEN_FORM.test(x.token) ? x.token : null,
    zuletzt_gesehen: istText(x.zuletzt_gesehen) ? x.zuletzt_gesehen : null,
    ...(Array.isArray(x.sportschulen) ? { sportschulen: x.sportschulen.filter(istText) } : {}),
  }];
}

function detailPruefen(d: PlanungsDetail): PlanungsDetail {
  return {
    ...d,
    html: istText(d.html) ? d.html : '',
    feedback: Array.isArray(d.feedback) ? d.feedback.map(eintragPruefen) : [],
    versionen: Array.isArray(d.versionen) ? d.versionen : [],
    sichtbar_fuer: Array.isArray(d.sichtbar_fuer) ? d.sichtbar_fuer.flatMap(sichtbarPruefen) : undefined,
  };
}

export async function ladePlanungListe(): Promise<{ darf_bearbeiten: boolean; mails: PlanungsMail[] }> {
  const r = await rpc<{ darf_bearbeiten: boolean; mails: PlanungsMail[] }>('mail_planung_liste');
  return { darf_bearbeiten: r?.darf_bearbeiten === true, mails: r?.mails ?? [] };
}

export async function ladePlanungMail(id: string): Promise<PlanungsDetail> {
  return detailPruefen(await rpc<PlanungsDetail>('mail_planung_mail', { p_campaign_id: id }));
}

export async function ladePlanungVersion(id: string, nr: number): Promise<VersionVoll> {
  const r = await rpc<{ version: VersionVoll }>('mail_planung_version', { p_campaign_id: id, p_nr: nr });
  return r.version;
}

export async function feedbackSenden(campaignId: string, daten: FeedbackDaten): Promise<string> {
  const r = await rpc<{ ok: boolean; id: string }>('mail_feedback_senden', { p_campaign_id: campaignId, p_daten: daten });
  return r.id;
}

/** Team: Eintrag erledigen/übernehmen/ablehnen; mit index nur eine Änderung (-1 = Betreff). 'offen' setzt zurück. */
export async function feedbackEntscheiden(feedbackId: string, ergebnis: FeedbackErgebnis | 'offen', index?: number | null): Promise<void> {
  await rpc('mail_feedback_entscheiden', { p_feedback_id: feedbackId, p_ergebnis: ergebnis, p_index: index ?? null });
}

export function feedbackGelesen(campaignId: string): Promise<number> {
  return rpc<number>('mail_feedback_gelesen', { p_campaign_id: campaignId });
}

export async function feedbackUngelesen(): Promise<number> {
  try { return Number(await rpc<number>('mail_feedback_ungelesen')) || 0; } catch { return 0; }
}

export async function feedbackAlle(limit = 200): Promise<FeedbackMitMail[]> {
  const r = await rpc<FeedbackMitMail[] | null>('mail_feedback_alle', { p_limit: limit });
  return Array.isArray(r) ? r.map(eintragPruefen) : [];
}

/**
 * Inhalt ändern (direkt bearbeitet, Vorschlag übernommen, Version
 * wiederhergestellt). Läuft mit den Rechten der Person (RLS) und nur, wenn
 * die Mail seit basisMd5 unverändert ist. Danach ggf. nach Brevo übertragen.
 */
export async function inhaltAendern(p: {
  campaignId: string; html: string; basisMd5: string; quelle: 'bearbeitet' | 'vorschlag' | 'wiederhergestellt';
  betreff?: string | null; vorschau?: string | null; notiz?: string | null;
}): Promise<{ md5: string; version_nr: number }> {
  return rpc('mail_inhalt_aendern', {
    p_campaign_id: p.campaignId, p_html: p.html, p_betreff: p.betreff ?? null, p_vorschau: p.vorschau ?? null,
    p_basis_md5: p.basisMd5, p_quelle: p.quelle, p_notiz: p.notiz ?? null, p_feedback_ids: [],
  });
}

export function versionWiederherstellen(campaignId: string, nr: number, basisMd5: string): Promise<{ md5: string; version_nr: number }> {
  return rpc('mail_version_wiederherstellen', { p_campaign_id: campaignId, p_nr: nr, p_basis_md5: basisMd5 });
}

/** Ein Event aus dem Event Manager zur Auswahl unter „Termine & Stand“. */
export interface EventAuswahl {
  id: string;
  titel: string;
  beginn: string | null;
  ende: string | null;
  /** eingereicht · in_pruefung · rueckfrage · in_planung · abgeschlossen */
  status: string;
  /** Die Mail hängt an diesem Event. */
  zugeordnet: boolean;
  /** Reiter „E-Mail“ des Events im Event Manager */
  link: string;
}

/** Events, denen das Team die Mail zuordnen kann — das zugeordnete markiert (Migration 0061). */
export async function ladeEventAuswahl(campaignId: string): Promise<EventAuswahl[]> {
  return (await rpc<EventAuswahl[] | null>('mail_event_auswahl', { p_campaign_id: campaignId })) ?? [];
}

/** Mail einem Event im Event Manager zuordnen; null löst die Zuordnung. */
export function eventZuordnen(campaignId: string, eventId: string | null): Promise<{ ok: boolean; geaendert: boolean }> {
  return rpc('mail_event_zuordnen', { p_campaign_id: campaignId, p_event_id: eventId });
}

/** Termine, Stand, Notiz einer Mail speichern (Team). */
export async function planungSpeichern(campaignId: string, felder: PlanungFelder): Promise<void> {
  const { error } = await client().from('Mail_planung').upsert({ campaign_id: campaignId, ...felder }, { onConflict: 'campaign_id' });
  if (error) throw new Error(error.message);
}

const PERSON_SPALTEN = 'id,name,email,rolle_text,sicht,sportschulen,token,aktiv,zuletzt_gesehen,created_at,updated_at,herkunft';

export async function listePersonen(): Promise<Ansprechperson[]> {
  const { data, error } = await client().from('Mail_ansprechpersonen').select(PERSON_SPALTEN).order('name');
  if (error) throw new Error(error.message);
  return (data ?? []) as Ansprechperson[];
}

export async function personSpeichern(p: Partial<Ansprechperson> & { name: string }): Promise<Ansprechperson> {
  const werte = {
    name: p.name.trim(), email: p.email?.trim() || null, rolle_text: p.rolle_text?.trim() || null,
    sicht: p.sicht ?? 'zugeordnet', sportschulen: p.sportschulen ?? [], aktiv: p.aktiv ?? true,
  };
  // Studioaccounts: Name, Adresse, Sicht und Sportschulen kommen aus dem CRM — nur Rolle und aktiv ändern.
  const q = p.id
    ? client().from('Mail_ansprechpersonen')
      .update(p.herkunft === 'studioaccount' ? { rolle_text: werte.rolle_text, aktiv: werte.aktiv } : werte).eq('id', p.id)
    : client().from('Mail_ansprechpersonen').insert(werte);
  const { data, error } = await q.select(PERSON_SPALTEN).single();
  if (error) throw new Error(error.message);
  return data as Ansprechperson;
}

export async function personLoeschen(id: string): Promise<void> {
  const { error } = await client().from('Mail_ansprechpersonen').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

/** Neuer Link: der alte funktioniert danach nicht mehr. */
export function neuerLink(personId: string): Promise<string> {
  return rpc<string>('mail_ansprechperson_neuer_link', { p_id: personId });
}

export async function personenVorschlaege(campaignId?: string | null): Promise<PersonVorschlag[]> {
  return (await rpc<PersonVorschlag[]>('mail_ansprechpersonen_vorschlaege', { p_campaign_id: campaignId ?? null })) ?? [];
}

/** Ansprechpersonen um Feedback zu einer Mail bitten (vorhandene bleiben, Nachricht wird aktualisiert). */
export async function feedbackAnfragen(campaignId: string, personIds: string[], nachricht?: string | null): Promise<void> {
  if (personIds.length === 0) return;
  const zeilen = personIds.map((person_id) => ({ campaign_id: campaignId, person_id, nachricht: nachricht?.trim() || null }));
  const { error } = await client().from('Mail_feedback_anfragen').upsert(zeilen, { onConflict: 'campaign_id,person_id' });
  if (error) throw new Error(error.message);
}

export async function anfrageEntfernen(campaignId: string, personId: string): Promise<void> {
  const { error } = await client().from('Mail_feedback_anfragen').delete().eq('campaign_id', campaignId).eq('person_id', personId);
  if (error) throw new Error(error.message);
}

/** Einladung mit dem persönlichen Link per Mail (nur wenn die Person eine Adresse hat). */
export function personEinladen(personId: string, campaignIds?: string[], nachricht?: string | null): Promise<{ ok: boolean; sent_to: number }> {
  return invokeFunction('notify-mail-feedback', {
    aktion: 'einladen', person_id: personId, campaign_ids: campaignIds ?? null, nachricht: nachricht?.trim() || null,
  });
}

/**
 * Mail löschen (Migration 0050, weich): verschwindet aus der Übersicht und
 * steht unter „Gelöscht". Eine Brevo-Kampagne im Entwurf oder angehalten
 * löscht brevo-draft mit; ist sie dort eingeplant, lehnt es ab (erst anhalten).
 */
export function mailLoeschen(campaignId: string): Promise<LoeschErgebnis> {
  return invokeFunction<LoeschErgebnis>('brevo-draft', { action: 'loeschen', campaign_id: campaignId });
}

/** Gelöschte Mails, die man sehen darf, neueste zuerst. */
export async function ladeGeloeschte(): Promise<GeloeschteMail[]> {
  const r = await rpc<GeloeschteMail[] | null>('mail_planung_geloescht');
  return Array.isArray(r) ? r.map((g) => ({ ...g, sportschulen: Array.isArray(g.sportschulen) ? g.sportschulen : [] })) : [];
}

/** Gelöschte Mail zurückholen: Stand von vorher (eingeplant → Entwurf), ohne Brevo-Kampagne. */
export function mailWiederherstellen(campaignId: string): Promise<{ ok: boolean; status: CampaignStatus; war_in_brevo: boolean }> {
  return rpc('mail_mail_wiederherstellen', { p_campaign_id: campaignId });
}

/** Mailtool-Stand nach Brevo übertragen; angehalten = auch in Brevo angehaltene Kampagnen. */
export function nachBrevoUebertragen(campaignId: string, angehalten = false, force = false): Promise<BrevoDraftResult> {
  return edgeFunctions.brevoDraft(campaignId, 'push', force, angehalten);
}

// ── Abrufe: Feedback-Link (Token + Google-Anmeldung) ────────────────────────

export type LinkFehler =
  | 'unbekannt' | 'keine_mail' | 'nicht_gefunden' | 'schon_bearbeitet' | 'hat_antworten'
  | 'anmeldung_noetig' | 'andere_adresse' | 'keine_adresse' | 'nicht_erlaubt';

/**
 * Antwort ohne passende Anmeldung (Migration 0051): keine Mails, keine
 * Inhalte — nur der Vorname für die Begrüßung (bei Studioaccounts der ganze
 * Name) und der Stand der Anmeldung mit der maskierten Adresse.
 */
export interface LinkAbgelehnt {
  ok: false;
  fehler: LinkFehler;
  person: { name: string } | null;
  anmeldung: LinkAnmeldung | null;
}
/**
 * admin_ansicht (Migration 0052): Ein Admin liest den Link einer anderen
 * Person mit — dieselben Inhalte, aber nur zum Ansehen. Nach dem Prüfen
 * immer gesetzt (true nur bei anmeldung.status 'admin_ansicht').
 */
type LinkAntwort<T> = ({ ok: true; admin_ansicht?: boolean } & T) | LinkAbgelehnt;
/** So kommt die Antwort aus der Datenbank, bevor sie geprüft ist. */
type RohAntwort<T> = ({ ok: true; admin_ansicht?: unknown } & T) | { ok: false; fehler: LinkFehler; person?: unknown; anmeldung?: unknown };

/**
 * 'nicht_erlaubt' = Adresse der Person nicht @randori-pro.de (Migration 0051).
 * 'admin_ansicht' = ein Admin sieht den Link einer anderen Person (Migration
 * 0052) — nur lesen, nie schreiben.
 */
export type LinkAnmeldeStatus = 'ok' | 'nicht_angemeldet' | 'andere_adresse' | 'keine_adresse' | 'nicht_erlaubt' | 'admin_ansicht';

/**
 * Wer den Link gerade nutzt: Ansehen und Schreiben (Kommentar, Stift,
 * Vorschlag, „Passt so", Zurückziehen) gehen nur mit status 'ok' —
 * angemeldet mit der Adresse der Person. Mit 'admin_ansicht' liest ein Admin
 * mit, schreiben geht dann nicht. `adresse` ist die maskierte Adresse der
 * Person („o•••@…"), `angemeldet_als` die eigene Adresse der angemeldeten
 * Person.
 */
export interface LinkAnmeldung {
  status: LinkAnmeldeStatus;
  adresse: string | null;
  angemeldet_als: string | null;
}

const ANMELDE_STATUS: readonly LinkAnmeldeStatus[] = ['ok', 'nicht_angemeldet', 'andere_adresse', 'keine_adresse', 'nicht_erlaubt', 'admin_ansicht'];
const OHNE_ANMELDUNG: LinkAnmeldung = { status: 'nicht_angemeldet', adresse: null, angemeldet_als: null };

/** Fehlt die Angabe oder ist sie kaputt: wie abgemeldet — die Oberfläche bietet nie an, was die Datenbank ablehnt. */
export function anmeldungPruefen(x: unknown): LinkAnmeldung {
  if (!istObjekt(x)) return OHNE_ANMELDUNG;
  const status = ANMELDE_STATUS.find((s) => s === x.status) ?? 'nicht_angemeldet';
  return {
    status,
    adresse: istText(x.adresse) && x.adresse.trim() ? x.adresse : null,
    angemeldet_als: istText(x.angemeldet_als) && x.angemeldet_als.trim() ? x.angemeldet_als : null,
  };
}

/** Darf über den Link geschrieben werden? Nur die Person selbst — nie in der Admin-Ansicht. */
export function darfUeberLinkSchreiben(a: LinkAnmeldung | null | undefined): boolean {
  return a?.status === 'ok';
}

/** Liest gerade ein Admin den Link einer anderen Person mit (nur ansehen)? */
export function istAdminAnsicht(a: LinkAnmeldung | null | undefined): boolean {
  return a?.status === 'admin_ansicht';
}

/**
 * Anmeldung einer erfolgreichen Link-Antwort: Meldet die Datenbank
 * admin_ansicht, gilt die Admin-Ansicht auch dann, wenn der Status fehlt —
 * außer die Person ist es selbst ('ok'). Die Admin-Ansicht schreibt nie.
 */
function linkAnmeldungLesen(r: { anmeldung?: unknown; admin_ansicht?: unknown }): { anmeldung: LinkAnmeldung; admin_ansicht: boolean } {
  const a = anmeldungPruefen(r.anmeldung);
  const admin = a.status === 'admin_ansicht' || (r.admin_ansicht === true && a.status !== 'ok');
  return { anmeldung: admin ? { ...a, status: 'admin_ansicht' } : a, admin_ansicht: admin };
}

/** Abgelehnte Antwort prüfen: Name und Anmeldung nur, wenn sie wirklich mitkommen. */
function abgelehntPruefen(r: { fehler: LinkFehler; person?: unknown; anmeldung?: unknown }): LinkAbgelehnt {
  const name = istObjekt(r.person) && istText(r.person.name) && r.person.name.trim() ? r.person.name.trim() : null;
  return {
    ok: false,
    fehler: r.fehler,
    person: name ? { name } : null,
    anmeldung: istObjekt(r.anmeldung) ? anmeldungPruefen(r.anmeldung) : null,
  };
}

/**
 * Ohne passende Anmeldung liefert der Link nur den Anmeldestand — dann zeigt
 * die Seite die Anmeldung statt der Mails. Liefert die Anmeldung oder null.
 */
export function linkAnmeldungFehlt(r: LinkAntwort<object> | undefined | null): LinkAnmeldung | null {
  if (!r || r.ok || !r.anmeldung || r.anmeldung.status === 'ok') return null;
  return r.anmeldung;
}

const ANMELDE_FEHLER = ['anmeldung_noetig', 'andere_adresse', 'keine_adresse', 'nicht_erlaubt'] as const;
type AnmeldeFehlerCode = typeof ANMELDE_FEHLER[number];

const LINK_FEHLER_TEXT: Partial<Record<LinkFehler, string>> = {
  anmeldung_noetig: 'Bitte melde Dich erst mit Deinem Google-Konto an.',
  andere_adresse: 'Du bist nicht mit dem Google-Konto angemeldet, für das dieser Link gilt.',
  keine_adresse: 'Für diesen Link ist keine Mailadresse hinterlegt. Bitte melde Dich bei der Redaktion.',
  nicht_erlaubt: `Diese Links gibt es nur für Adressen @${ANMELDE_DOMAIN}. Bitte melde Dich bei der Redaktion.`,
  hat_antworten: 'Darauf wurde schon geantwortet — zurückziehen geht nicht mehr.',
  schon_bearbeitet: 'Das Team hat darüber schon entschieden.',
  unbekannt: 'Der Link ist nicht mehr gültig.',
};

/** Schreiben über den Link scheiterte an der Anmeldung (Sitzung abgelaufen, anderes Konto …). */
export class LinkAnmeldeFehler extends Error {
  readonly fehler: AnmeldeFehlerCode;
  constructor(fehler: AnmeldeFehlerCode) {
    super(LINK_FEHLER_TEXT[fehler]);
    this.name = 'LinkAnmeldeFehler';
    this.fehler = fehler;
  }
}

export function istLinkAnmeldeFehler(e: unknown): e is LinkAnmeldeFehler {
  return e instanceof LinkAnmeldeFehler;
}

function linkFehlerWerfen(fehler: LinkFehler, sonst: string): never {
  if ((ANMELDE_FEHLER as readonly string[]).includes(fehler)) throw new LinkAnmeldeFehler(fehler as AnmeldeFehlerCode);
  throw new Error(LINK_FEHLER_TEXT[fehler] ?? sonst);
}

export async function linkUebersicht(token: string): Promise<LinkAntwort<{ person: LinkPerson; mails: PlanungsMail[]; jetzt: string; anmeldung: LinkAnmeldung }>> {
  const r = await rpc<RohAntwort<{ person: LinkPerson; mails: PlanungsMail[]; jetzt: string; anmeldung?: unknown }>>('mail_link_uebersicht', { p_token: token });
  return r.ok ? { ...r, ...linkAnmeldungLesen(r) } : abgelehntPruefen(r);
}

export async function linkMail(token: string, campaignId: string): Promise<LinkAntwort<{
  person: LinkPerson; mail: PlanungsDetail;
  anfrage: { nachricht: string | null; angefragt_von_name: string | null; angefragt_am: string; antwort: AnfrageAntwort; gesehen_am: string | null } | null;
  anmeldung: LinkAnmeldung;
}>> {
  const r = await rpc<RohAntwort<{
    person: LinkPerson; mail: PlanungsDetail;
    anfrage: { nachricht: string | null; angefragt_von_name: string | null; angefragt_am: string; antwort: AnfrageAntwort; gesehen_am: string | null } | null;
    anmeldung?: unknown;
  }>>('mail_link_mail', { p_token: token, p_campaign_id: campaignId });
  return r.ok ? { ...r, mail: detailPruefen(r.mail), ...linkAnmeldungLesen(r) } : abgelehntPruefen(r);
}

export async function linkVersion(token: string, campaignId: string, nr: number): Promise<VersionVoll> {
  const r = await rpc<RohAntwort<{ version: VersionVoll; anmeldung?: unknown }>>('mail_link_version', { p_token: token, p_campaign_id: campaignId, p_nr: nr });
  if (!r.ok) linkFehlerWerfen(r.fehler, 'Version nicht gefunden');
  return r.version;
}

/** Wirft LinkAnmeldeFehler, wenn die Anmeldung nicht (mehr) passt. */
export async function linkSenden(token: string, campaignId: string, daten: FeedbackDaten): Promise<string> {
  const r = await rpc<RohAntwort<{ id: string }>>('mail_link_senden', { p_token: token, p_campaign_id: campaignId, p_daten: daten });
  if (!r.ok) linkFehlerWerfen(r.fehler, 'Der Link ist nicht mehr gültig.');
  return r.id;
}

/** Wirft LinkAnmeldeFehler, wenn die Anmeldung nicht (mehr) passt. */
export async function linkZurueckziehen(token: string, feedbackId: string): Promise<void> {
  const r = await rpc<RohAntwort<object>>('mail_link_zurueckziehen', { p_token: token, p_feedback_id: feedbackId });
  if (!r.ok) linkFehlerWerfen(r.fehler, 'Zurückziehen ging nicht.');
}

/** Ein eigener Feedback-Link der angemeldeten Person (mail_meine_links, Migration 0051). */
export interface MeinLink {
  name: string;
  token: string;
  sicht: PersonSicht;
  sportschulen: string[];
}

/**
 * Eigene Feedback-Links — nur mit Google-Anmeldung unter der Adresse der
 * Person (sonst leer). Für „Kein Zugang“: Studioaccounts kommen so zu den
 * Mails ihrer Sportschule.
 */
export async function meineLinks(): Promise<MeinLink[]> {
  const r = await rpc<unknown>('mail_meine_links', {});
  if (!Array.isArray(r)) return [];
  return r.flatMap((x): MeinLink[] => {
    if (!istObjekt(x) || !istText(x.token) || !/^[0-9a-f]{64}$/.test(x.token) || !istText(x.name)) return [];
    const sicht: PersonSicht = x.sicht === 'alle' || x.sicht === 'sportschulen' ? x.sicht : 'zugeordnet';
    const sportschulen = Array.isArray(x.sportschulen) ? x.sportschulen.filter(istText) : [];
    return [{ name: x.name, token: x.token, sicht, sportschulen }];
  });
}

// ── Werkstatt-Adapter ───────────────────────────────────────────────────────

/**
 * Was die Mail-Werkstatt (Mail + Kommentarspalte + Versionen) können darf.
 * Team-Seite und Feedback-Link bauen je einen Adapter; die Werkstatt selbst
 * kennt keine Abrufe.
 */
export interface WerkstattAdapter {
  art: 'team' | 'link';
  /** Name, unter dem neue Einträge erscheinen */
  ichName: string;
  /** Annehmen/Ablehnen, Erledigen, direkt Bearbeiten, Versionen wiederherstellen */
  darfEntscheiden: boolean;
  /** Admin im Mailtool: darf den Prompt für Claude Code erzeugen (nur Team-Seite) */
  istAdmin?: boolean;
  /**
   * Fehlt `senden`, ist die Werkstatt nur zum Lesen da: kein Kommentarfeld,
   * kein Stift, kein „Text ändern“, kein „Passt so“, keine Antworten
   * (Feedback-Link ohne passende Anmeldung).
   */
  senden?(daten: FeedbackDaten): Promise<string>;
  /** Hinweis in der Kommentarspalte, wenn `senden` fehlt */
  nurLesenHinweis?: string;
  zurueckziehen?(feedbackId: string): Promise<void>;
  entscheiden?(feedbackId: string, ergebnis: FeedbackErgebnis | 'offen', index?: number | null): Promise<void>;
  version(nr: number): Promise<VersionVoll>;
  /** Neuen Inhalt speichern (Team); liefert neuen md5 — danach lädt die Seite neu. */
  inhaltSpeichern?(p: { html: string; basisMd5: string; quelle: 'bearbeitet' | 'vorschlag' | 'wiederhergestellt'; betreff?: string | null; notiz?: string | null }): Promise<{ md5: string; version_nr: number }>;
  wiederherstellen?(nr: number, basisMd5: string): Promise<void>;
  /** Nach jeder Änderung: Daten neu laden */
  neuLaden(): Promise<void> | void;
}
